import { telemetryIdsFromUrl } from './telemetry-identifiers';
import { rememberRequest } from './request-correlation';
import {
  logCorrelationId,
  logFailureReason,
  StructuredLogger,
} from './structured-logger.service';

// Only literal templates cross the telemetry boundary. Unknown endpoints never
// fall back to the URL, which may contain client identifiers or search terms.
import { API_ROUTE_TEMPLATES } from './structured-log-policy';

export function apiRouteTemplate(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0];
  return (
    API_ROUTE_TEMPLATES.find((template) => {
      if (template === '/unclassified') return false;
      const pattern = template.replace(/:[a-z]+/g, '[^/]+');
      return new RegExp(`^${pattern}$`).test(pathname);
    }) ?? '/unclassified'
  );
}

export function reportApiFailure(
  logger: StructuredLogger,
  method: string,
  path: string,
  error: unknown,
  started: number,
): void {
  const pathname = path.split(/[?#]/, 1)[0];
  const detail = error as {
    status?: number;
    error?: { statusCode?: number; result?: { status?: number } };
  } | null;
  if (
    method === 'get' &&
    pathname === '/financialsnapshot' &&
    (detail?.status === 204 ||
      detail?.error?.statusCode === 204 ||
      detail?.error?.result?.status === 204)
  )
    return;
  // Expected no-record responses are not failures of the inventory operation.
  if (
    method === 'get' &&
    pathname === '/accounts' &&
    logFailureReason(error).http_status === 404
  )
    return;
  const requestId = logCorrelationId();
  rememberRequest(error, requestId);
  logger.emit('api.request.failed', {
    feature: 'api',
    stage: 'request',
    outcome: 'failed',
    method: method.toUpperCase(),
    route_template: apiRouteTemplate(path),
    ...telemetryIdsFromUrl(path),
    request_id: requestId,
    duration_ms: Math.max(0, Date.now() - started),
    ...logFailureReason(error),
  });
}

export type ResponseContract =
  | 'accounts.inventory'
  | 'accounts.detail'
  | 'accounts.transactions'
  | 'financial_snapshot.template'
  | 'financial_snapshot.snapshot';

export function reportContractMismatch(
  logger: StructuredLogger,
  contract: ResponseContract,
): void {
  logger.emit('api.response.contract_mismatch', {
    feature: 'api',
    stage: 'response',
    outcome: 'failed',
    reason_code: 'contract_mismatch',
    contract_name: contract,
  });
}
